import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packages, type Package } from '../../api';

export function PackagesPage() {
  const navigate = useNavigate();
  const [list, setList] = useState<Package[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [uploadFor, setUploadFor] = useState<Package | null>(null);

  async function refresh() {
    const r = await packages.list();
    setList(r.packages);
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <div className="toolbar">
        <h1>Packages</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreate(true)}>+ New package</button>
      </div>

      {list.length === 0 ? (
        <div className="empty">No packages yet. Create one to open the content editor.</div>
      ) : list.map((p) => (
        <div className="card" key={p.id}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <strong>{p.name}</strong>
              <div style={{ color: 'var(--fg-3)', fontSize: '0.9em' }}>{p.description ?? '—'}</div>
            </div>
            <button className="primary" onClick={() => navigate(`/packages/${p.id}/edit`)}>Open editor</button>
            <button style={{ marginLeft: '0.4em' }} onClick={() => setUploadFor(p)}>Upload raw bundle</button>
          </div>
          {p.versions && p.versions.length > 0 && (
            <table style={{ marginTop: '0.8em' }}>
              <thead><tr><th>Version</th><th>Size</th><th>Uploaded</th><th>ID</th></tr></thead>
              <tbody>
                {p.versions.map((v) => (
                  <tr key={v.id}>
                    <td>v{v.version}</td>
                    <td>{(v.size_bytes / 1024 / 1024).toFixed(2)} MB</td>
                    <td>{new Date(v.created_at).toLocaleString()}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8em', color: 'var(--fg-3)' }}>{v.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {showCreate && <CreatePackageModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
      {uploadFor && <UploadVersionModal pkg={uploadFor} onClose={() => setUploadFor(null)} onUploaded={() => { setUploadFor(null); refresh(); }} />}
    </div>
  );
}

function CreatePackageModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      await packages.create({ name, description: description || undefined });
      onCreated();
    } finally { setBusy(false); }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>New package</h2>
        <div className="field"><label>Name</label><input autoFocus value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>Description (optional)</label><input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!name || busy} onClick={submit}>Create</button>
        </div>
      </div>
    </div>
  );
}

function UploadVersionModal({ pkg, onClose, onUploaded }: { pkg: Package; onClose: () => void; onUploaded: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [manifest, setManifest] = useState<string>(JSON.stringify({ entry: 'index.html', renderer: 'chromium' }, null, 2));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      let parsed: unknown;
      try { parsed = JSON.parse(manifest); }
      catch { setErr('Manifest is not valid JSON'); return; }
      await packages.uploadVersion(pkg.id, file, parsed);
      onUploaded();
    } catch (e) {
      setErr('Upload failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 560 }}>
        <h2 style={{ marginTop: 0 }}>Upload new version of {pkg.name}</h2>
        <div className="field">
          <label>Bundle (.zip containing index.html + assets)</label>
          <input ref={fileRef} type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
        <div className="field">
          <label>Manifest JSON</label>
          <textarea rows={8} value={manifest} onChange={(e) => setManifest(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: '0.85em' }} />
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!file || busy} onClick={submit}>
            {busy ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
